// "Watch while it downloads": a <video> fed by the progressive player (./progressive.js) from the pieces this browser
// has already VERIFIED. Offered only for MP4 video after the file's own access gate opened (password, denied, expired
// and missing never reach this) and, on owning sites, only when the site's context says `allowPreview: true`.
// Closing it stops playback, cancels an unfinished transfer and leaves the peer swarm — nothing keeps downloading or
// uploading in the background. Sharing stays exactly what the engine allows (public + visitor opt-in only).
import { createProgressivePlayer } from './progressive.js';

export const PLAYABLE_MIME = /^video\/mp4$/i;

/** Whether the panel can be offered for this engine at all (the player itself still checks codecs). */
export function canOfferPlayback(engine, win = globalThis, { allowPreview = true } = {}) {
  if (allowPreview !== true) return false;
  const m = engine && engine.manifest;
  return !!(m && PLAYABLE_MIME.test(m.mime) && m.sizeBytes > 0 && engine.hybridEnabled && win.MediaSource && typeof win.MediaSource.isTypeSupported === 'function');
}

/**
 * @param {HTMLElement} host  where the panel is appended
 * @param {() => any} engine  the file's engine
 * @param {{ text: Record<string, string>, onClosed?: () => void, className?: string, closeClass?: string,
 *           allowPreview?: boolean }} o
 */
export function createMediaPanel(host, engine, { text, onClosed, className = 'solred-player', closeClass = '', allowPreview = true } = {}) {
  let player = null, section = null, unplayable = false;
  const T = { title: 'Playback', close: 'Close', starting: 'Preparing…', buffering: 'Loading verified pieces…', playing: '', complete: '', unsupported: 'This video cannot play while it downloads; it keeps downloading.', error: 'Playback stopped.', ...text };

  function render() {
    section = document.createElement('section');
    section.className = className;
    section.setAttribute('aria-label', T.title);
    section.innerHTML = `<video controls playsinline preload="none"></video><div class="${className}-bar"><p class="${className}-state" role="status"></p><button type="button" class="${closeClass} ${className}-close"></button></div>`;
    section.querySelector(`.${className}-close`).textContent = T.close;
    section.querySelector(`.${className}-close`).addEventListener('click', () => close());
    host.appendChild(section);
    return section.querySelector('video');
  }

  /** Call from the visitor's click (autoplay is allowed by that gesture). */
  async function open() {
    if (player) return player.state;
    const e = engine();
    if (unplayable || !canOfferPlayback(e, globalThis, { allowPreview })) return 'unsupported';
    const video = render();
    const stateEl = section.querySelector(`.${className}-state`);
    video.autoplay = true;
    player = createProgressivePlayer({
      engine: e, video,
      onState: (s) => {
        stateEl.textContent = T[s] ?? '';
        if (section) section.dataset.state = s;
        if (s === 'unsupported') unplayable = true; // not offered again for this file
        if (s === 'unsupported' || s === 'error') { // the normal verified download continues; only the panel goes
          const p = player; player = null; setTimeout(() => { if (!player) removePanel(); }, s === 'unsupported' ? 4000 : 6000); void p;
        }
      },
    });
    if (e.status === 'idle' || e.status === 'cancelled') void e.start();
    return player.start();
  }

  function removePanel() { if (section) { section.remove(); section = null; } }

  /** Stop playback; an unfinished transfer is cancelled and the swarm left (no hidden download or upload). */
  async function close() {
    const e = engine();
    if (player) { player.close(); player = null; }
    removePanel();
    if (e) {
      if (e.sharing) e.toggleSharing();
      if (!['ready', 'saving', 'saved', 'save-error', 'idle', 'cancelled'].includes(e.status)) await e.cancel();
    }
    onClosed?.();
  }

  return { open, close, get state() { return player ? player.state : unplayable ? 'unsupported' : 'closed'; } };
}

export const PLAYBACK_TEXT = Object.freeze({
  en: { offer: 'Watch while it downloads', title: 'Playback', close: 'Close video', starting: 'Preparing…', buffering: 'Loading verified pieces…', unsupported: 'This video cannot play while it downloads; the download continues.', error: 'Playback stopped.' },
  es: { offer: 'Ver mientras se descarga', title: 'Reproducción', close: 'Cerrar vídeo', starting: 'Preparando…', buffering: 'Cargando piezas verificadas…', unsupported: 'Este vídeo no se puede ver mientras se descarga; la descarga continúa.', error: 'La reproducción se detuvo.' },
});

/**
 * A "watch while it downloads" button + panel under an embedded transfer view (owning sites, Drive Weave). Shown only
 * when `allowPreview === true` (the site's own preview permission) and the file is an MP4 the engine can serve.
 * @returns {{ destroy: () => void }}
 */
export function createPlaybackOffer(container, engine, { allowPreview = false, lang = 'en', label } = {}) {
  const T = PLAYBACK_TEXT[lang] || PLAYBACK_TEXT.en;
  if (allowPreview !== true || !container) return { destroy() {} };
  const bar = document.createElement('div');
  bar.className = 'solred-playbar';
  const btn = document.createElement('button');
  btn.type = 'button'; btn.className = 'solred-player-btn solred-play'; btn.hidden = true;
  btn.textContent = `▶ ${T.offer}${label ? ` · ${label}` : ''}`;
  bar.appendChild(btn);
  container.appendChild(bar);
  const panel = createMediaPanel(bar, () => engine, { text: T, className: 'solred-player', closeClass: 'solred-player-btn', allowPreview: true, onClosed: () => sync() });
  function sync() { btn.hidden = !canOfferPlayback(engine) || panel.state !== 'closed'; }
  btn.addEventListener('click', () => { void panel.open().then(sync); sync(); });
  const off = engine.subscribe(() => sync());
  sync();
  return { destroy() { off(); void panel.close(); bar.remove(); } };
}

/** Neutral styles for the embedded player (owning views inject them once). */
export const PLAYBACK_CSS = `.solred-playbar{display:grid;gap:10px;margin:12px 0}.solred-playbar [hidden]{display:none!important}
.solred-player{display:grid;gap:8px}.solred-player video{width:100%;max-height:70vh;background:#000;border-radius:10px;display:block}
.solred-player-bar{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}.solred-player-state{margin:0;font-size:14px;opacity:.85;min-height:1.4em}
.solred-player-btn{font:inherit;font-size:14px;min-height:44px;padding:0 16px;border-radius:999px;border:1px solid currentColor;background:transparent;color:inherit;cursor:pointer;justify-self:start}
.solred-player-btn:focus-visible{outline:3px solid #2f80ed;outline-offset:3px}`;
